"use client";

import { useRef, type ReactNode } from "react";

const MAX_TILT = 5; // degrees

/**
 * Presentational tile wrapper. On mouse move it writes tilt (--rx/--ry) and
 * cursor position (--mx/--my) CSS vars and flips data-tilt="on"; CSS applies
 * the transform + gold glow, but only under (hover: hover) and (pointer: fine)
 * and not prefers-reduced-motion. Touch pointers are ignored, so on mobile the
 * tile is just a static card with the shared chrome.
 */
export default function BentoTile({
  className = "",
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  function handleMove(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType !== "mouse") return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; // 0..1
    const py = (e.clientY - rect.top) / rect.height; // 0..1
    el.style.setProperty("--ry", `${(px - 0.5) * 2 * MAX_TILT}deg`);
    el.style.setProperty("--rx", `${-(py - 0.5) * 2 * MAX_TILT}deg`);
    el.style.setProperty("--mx", `${px * 100}%`);
    el.style.setProperty("--my", `${py * 100}%`);
    el.dataset.tilt = "on";
  }

  function handleLeave() {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
    el.dataset.tilt = "off";
  }

  return (
    <div
      ref={ref}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      className={`bento-tile ${className}`}
    >
      <span className="bento-glow" aria-hidden="true" />
      <div className="bento-body">{children}</div>
    </div>
  );
}

